import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
    [
        'inline-flex items-center justify-center rounded-full',
        'border border-transparent px-2 py-0.5',
        'text-xs font-medium w-fit whitespace-nowrap shrink-0',
        '[&>svg]:size-3 gap-1 [&>svg]:pointer-events-none',
        'focus-visible:border-ring focus-visible:ring-ring/50',
        'focus-visible:ring-[3px]',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        'aria-invalid:border-destructive',
        'transition-[color,box-shadow] overflow-hidden',
    ].join(' '),
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
                secondary:
                    'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
                error: 'bg-error text-secondary [a&]:hover:bg-error/90 focus-visible:ring-error/20 dark:focus-visible:ring-error/40 dark:bg-error/60',
                warning:
                    'bg-warning text-secondary-foreground [a&]:hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40 dark:bg-warning/60',
                info: 'bg-info text-secondary-foreground [a&]:hover:bg-info/90 focus-visible:ring-info/20 dark:focus-visible:ring-info/40 dark:bg-info/60',
                outline:
                    'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
                ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 [a&]:hover:underline',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
)

/**
 * @example
 * ```tsx
 * <div className="h-full flex items-center overflow-visible relative">
 *   <Badge className="absolute top-1.5 -right-2 size-5 select-none cursor-pointer">
 *     3
 *   </Badge>
 *   <Button> // Trigger component
 *     Inbox
 *   </Button>
 * </div>
 */
function Badge({
    className,
    variant = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'span'> &
    VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : 'span'

    if (!props.children) return null

    return (
        <Comp
            data-slot="badge"
            data-variant={variant}
            className={cn(badgeVariants({ variant }), className)}
            {...props}
        />
    )
}

export { Badge, badgeVariants }
